import { useRef, useState } from 'react';

import { errorMessage } from '@/api/client';
import { useIncidents } from '@/api/queries';
import { NotFoundPage } from '@/app/errors';
import { useScopeParam } from '@/app/scope';
import { useCan } from '@/auth/roles';
import { Button } from '@/components/Button';
import { HealthBadge, Tag } from '@/components/badges';
import { CheckCircle, ExternalLink, Plus, Refresh } from '@/components/icons';
import { DIALOG_LINGER_MS, useLingering } from '@/components/motion';
import { EmptyState, ErrorNotice, Loading, Panel } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { HEALTH_LABEL } from '@/domain/health';
import type { Health, Incident, IncidentComponent, IncidentStage, Scope } from '@/domain/types';

import { IncidentUpdateDialog, OpenIncidentDialog, ResolveIncidentDialog } from './dialogs';

/**
 * Open incidents (TDD §5.3): the page's public incidents/unresolved.json, shown as customers
 * see it. The console reads it only to offer choices; the publisher re-checks through the
 * admin API before writing, so a response up to about 30 seconds old is acceptable.
 */
export function IncidentsPage() {
  const scope = useScopeParam();
  if (!scope) return <NotFoundPage />;
  return <Incidents scope={scope} />;
}

/**
 * The dialog on screen: which action, and for an update or a close, which incident. `seq` is
 * new on every opening, so a dialog opened again while the last is still fading out starts
 * with an empty form.
 */
type Action = { seq: number } & (
  { kind: 'open' } | { kind: 'update'; incident: Incident } | { kind: 'close'; incident: Incident }
);

type DistributiveOmit<T, K extends string> = T extends unknown ? Omit<T, K> : never;

function Incidents({ scope }: { scope: Scope }) {
  const query = useIncidents(scope);
  const canAct = useCan('approver');
  const [action, setAction] = useState<Action | null>(null);
  const opened = useRef(0);
  const show = (next: DistributiveOmit<Action, 'seq'>) =>
    setAction({ ...next, seq: ++opened.current });
  // The dialog stays mounted, closed, for a moment so it can fade out; it also outlives the
  // card it was opened from, which leaves the list once its incident is closed.
  const shown = useLingering(action, DIALOG_LINGER_MS);
  const close = () => setAction(null);

  let content: React.ReactNode;
  if (query.isLoading) {
    content = <Loading label="Reading the public page…" />;
  } else if (query.isError) {
    content = (
      <ErrorNotice
        title="Could not read the page's incidents"
        message={errorMessage(query.error)}
        action={
          <Button size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        }
      />
    );
  } else if (!query.data || query.data.incidents.length === 0) {
    content = (
      <Panel bodyClassName="p-0">
        <EmptyState title="No open incidents on this page" />
      </Panel>
    );
  } else {
    content = (
      <div className="flex flex-col gap-4">
        {query.data.incidents.map((incident) => (
          <IncidentCard
            key={incident.id}
            incident={incident}
            canAct={canAct}
            onUpdate={() => show({ kind: 'update', incident })}
            onClose={() => show({ kind: 'close', incident })}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-heading font-medium">Open incidents</h2>
        <div className="flex items-center gap-3 text-body text-ink-3 shrink-0">
          {query.data ? (
            <span>
              Fetched <Timestamp iso={query.data.fetched_at} mode="clock" withSeconds />
            </span>
          ) : null}
          <Button
            size="sm"
            icon={<Refresh size={14} />}
            onClick={() => void query.refetch()}
            loading={query.isFetching && !query.isLoading}
          >
            Refresh
          </Button>
          {canAct ? (
            <Button
              variant="primary"
              size="sm"
              icon={<Plus size={14} />}
              onClick={() => show({ kind: 'open' })}
            >
              New incident…
            </Button>
          ) : null}
        </div>
      </header>

      {content}

      {shown?.kind === 'open' ? (
        <OpenIncidentDialog
          key={shown.seq}
          scope={scope}
          open={action?.kind === 'open'}
          onClose={close}
        />
      ) : null}
      {shown?.kind === 'update' ? (
        <IncidentUpdateDialog
          key={shown.seq}
          scope={scope}
          incident={shown.incident}
          open={action?.kind === 'update'}
          onClose={close}
        />
      ) : null}
      {shown?.kind === 'close' ? (
        <ResolveIncidentDialog
          key={shown.seq}
          scope={scope}
          incident={shown.incident}
          open={action?.kind === 'close'}
          onClose={close}
        />
      ) : null}
    </div>
  );
}

const STAGE_LABEL: Record<IncidentStage, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

const IMPACT_LABEL: Record<Incident['impact'], string> = {
  none: 'No impact',
  minor: 'Minor impact',
  major: 'Major impact',
  critical: 'Critical impact',
};

/** Statuspage's component statuses, translated back to the Health vocabulary (TDD §1.6). */
const STATUSPAGE_TO_HEALTH: Record<string, Health> = {
  operational: 'ok',
  degraded_performance: 'impaired',
  partial_outage: 'degraded',
  major_outage: 'failed',
  under_maintenance: 'maintenance',
};

/** Every listed component shows operational on the page: nothing keeps the incident open but a person. */
function readyToResolve(incident: Incident): boolean {
  return (
    incident.components.length > 0 && incident.components.every((c) => c.status === 'operational')
  );
}

function IncidentCard({
  incident,
  canAct,
  onUpdate,
  onClose,
}: {
  incident: Incident;
  canAct: boolean;
  onUpdate: () => void;
  onClose: () => void;
}) {
  const updates = [...incident.incident_updates].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  );
  return (
    <Panel
      as="article"
      title={incident.name}
      description={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <StageChip stage={incident.status} />
          {readyToResolve(incident) ? (
            <Tag tone="success" icon={<CheckCircle size={12} />}>
              All components recovered: ready to resolve
            </Tag>
          ) : null}
          <span>{IMPACT_LABEL[incident.impact]}</span>
          <span>
            Opened <Timestamp iso={incident.created_at} />
          </span>
          <span>
            Updated <Timestamp iso={incident.updated_at} />
          </span>
        </span>
      }
      actions={
        <>
          <a
            href={incident.shortlink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-body whitespace-nowrap"
          >
            Open in Statuspage <ExternalLink size={14} />
          </a>
          {canAct ? (
            <>
              <Button size="sm" onClick={onUpdate}>
                Post update…
              </Button>
              <Button size="sm" onClick={onClose}>
                Close incident…
              </Button>
            </>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div>
          <h3 className="text-meta font-medium text-ink-3 mb-2">Affected components</h3>
          {incident.components.length > 0 ? (
            <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
              {incident.components.map((component) => (
                <li key={component.id}>
                  <ComponentChip component={component} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-body text-ink-3">No components attached.</p>
          )}
        </div>
        <div>
          <h3 className="text-meta font-medium text-ink-3 mb-2">Updates</h3>
          {updates.length > 0 ? (
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {updates.map((update) => (
                <li
                  key={update.id}
                  className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-[minmax(150px,max-content)_1fr]"
                >
                  <div className="flex flex-wrap items-center gap-2 text-meta text-ink-3">
                    <StageChip stage={update.status} />
                    <Timestamp iso={update.created_at} />
                  </div>
                  <p className="m-0 min-w-0 break-words text-body text-ink">{update.body}</p>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-body text-ink-3">No updates yet.</p>
          )}
        </div>
      </div>
    </Panel>
  );
}

/** An incident stage is a neutral tag with a dot: it is a phase, not a health. */
function StageChip({ stage }: { stage: IncidentStage }) {
  return (
    <Tag icon={<span className="size-1.5 rounded-full bg-current" aria-hidden="true" />}>
      {STAGE_LABEL[stage] ?? humanise(stage)}
    </Tag>
  );
}

function ComponentChip({ component }: { component: IncidentComponent }) {
  const health = STATUSPAGE_TO_HEALTH[component.status];
  if (health) {
    return <HealthBadge health={health} label={`${component.name}: ${HEALTH_LABEL[health]}`} />;
  }
  return (
    <Tag>
      {component.name}: {humanise(component.status)}
    </Tag>
  );
}

function humanise(value: string): string {
  const words = value.replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
